// src/lib/courriel-facture.js — le courriel qui accompagne une facture, relu
// et modifiable AVANT de partir.
//
// ⚠️ CHEMIN ARGENT. Module PUR : aucun envoi, aucun numéro tiré ici.
//
// Décisions Dimitri :
//   · 2026-09-30 — avant tout envoi au propriétaire, une fenêtre montre
//     l'objet et le texte du courriel, modifiables, avec des variables ; rien
//     ne part sans son clic après relecture ;
//   · 2026-10-03 — le courriel est SIMPLE et POLI, la facture part en PDF
//     JOINT (plus de facture recopiée dans le corps, plus de lien). Loi
//     Hoguet : « conciergerie », jamais « gestion / gérer / mandat ».
//
// Le numéro n'existe pas encore quand la fenêtre s'ouvre (il n'est tiré qu'au
// clic de confirmation) : la fenêtre montre donc « {numero} » comme
// « (attribuée à l'envoi) ». Le serveur (lib/facture-envoi-proprio.js) refuse
// un courriel où cette mention, ou une accolade, serait restée.
//
// Les variables sont en français, entre accolades, et en LISTE FERMÉE : une
// accolade inconnue (faute de frappe, « {numéro} ») bloque l'envoi AVANT que
// le numéro soit tiré — sinon le propriétaire recevrait « {numéro} » tel quel.

export const VARIABLES_COURRIEL_FACTURE = Object.freeze([
  { cle: "proprietaire", libelle: "nom du propriétaire" },
  { cle: "logement", libelle: "logement" },
  { cle: "mois", libelle: "mois facturé" },
  { cle: "numero", libelle: "numéro de facture (attribué à l'envoi)" },
  { cle: "montant", libelle: "montant de la facture" },
]);

const CLES = new Set(VARIABLES_COURRIEL_FACTURE.map((v) => v.cle));

// Texte PROPOSÉ par défaut (décision Dimitri 2026-10-03 : simple et poli,
// signé « L'équipe FULLBNBZEN »,
// PDF joint) — modifiable à chaque envoi, dans la fenêtre de relecture.
export const MODELE_COURRIEL_FACTURE = Object.freeze({
  objet: "Facture {numero} — {logement} — {mois}",
  texte:
    "Bonjour {proprietaire},\n\n" +
    "Veuillez trouver ci-joint la facture {numero} de notre conciergerie pour {logement} ({mois}), d'un montant de {montant}.\n\n" +
    "Nous restons à votre disposition pour toute question.\n\n" +
    "Bien cordialement,\nL'équipe FULLBNBZEN",
});

// Doit rester égal à NUMERO_A_VENIR de lib/facture-envoi-proprio.js.
// Accordé avec « la facture » (captures #1559).
export const NUMERO_A_VENIR = "(attribuée à l'envoi)";

export const OBJET_MAX = 120;
export const TEXTE_MAX = 4000;

// TOUTE paire d'accolades, espaces compris : « { numero } » n'est PAS
// « {numero} » — elle serait partie telle quelle. Elle est donc inconnue.
const RE_VAR = /\{([^{}]*)\}/g;

/** Les accolades qui ne sont pas des variables connues (dans l'ordre, sans doublon). */
export function variablesInconnues(texte) {
  const vues = [];
  for (const m of String(texte || "").matchAll(RE_VAR)) {
    if (!CLES.has(m[1]) && !vues.includes(m[0])) vues.push(m[0]);
  }
  return vues;
}

/**
 * Remplace les variables. `valeurs.numero` absent → « (attribuée à l'envoi) ».
 * Une accolade inconnue reste telle quelle (la validation l'a déjà refusée).
 */
export function remplirModele(texte, valeurs = {}) {
  return String(texte || "").replace(RE_VAR, (tout, cle) => {
    if (!CLES.has(cle)) return tout;
    if (cle === "numero" && !valeurs.numero) return NUMERO_A_VENIR;
    const v = valeurs[cle];
    return v == null ? "" : String(v);
  });
}

/**
 * Revue 2026-10-03 (point D) : le courriel REMPLI avec les vraies valeurs
 * (le numéro n'existe pas encore : un numéro d'essai le remplace) ne doit
 * garder aucune accolade — une valeur qui en contient (nom de logement…)
 * serait refusée par le serveur APRÈS le tirage du numéro.
 * @returns {{ ok: boolean, raison: string|null }}
 */
export function courrielRempliValide({ objet, texte }, valeurs = {}) {
  const pleines = { ...valeurs, numero: "P-0000-0000" };
  for (const v of [remplirModele(objet, pleines), remplirModele(texte, pleines)]) {
    if (/\{[^{}]*\}/.test(v) || v.includes(NUMERO_A_VENIR))
      return {
        ok: false,
        raison:
          "Le courriel rempli garde une accolade (dans une valeur comme le nom du logement) : corrige le texte avant d'envoyer.",
      };
  }
  return { ok: true, raison: null };
}

/**
 * Le montant relu dans la fenêtre est-il toujours celui de la facture ?
 * Revue 2026-10-03 (point 3) : comparé au centime AVANT de tirer le numéro.
 */
export function montantsConcordent(relu, maintenant) {
  if (!Number.isFinite(relu) || !Number.isFinite(maintenant)) return false;
  return Math.round(relu * 100) === Math.round(maintenant * 100);
}

/**
 * Le courriel peut-il partir ? Vérifié AVANT de tirer le numéro.
 * @returns {{ ok: boolean, raison: string|null }}
 */
export function validerCourriel({ objet, texte }) {
  if (!String(objet || "").trim())
    return { ok: false, raison: "L'objet du courriel est vide." };
  if (/[\r\n]/.test(String(objet || "").trim()))
    return { ok: false, raison: "L'objet tient sur une seule ligne." };
  if (!String(texte || "").trim())
    return { ok: false, raison: "Le texte du courriel est vide." };
  // Plus court que la limite du serveur (200 / 5 000 une fois rempli) : un
  // refus ici ne coûte rien, un refus du serveur arrive APRÈS le numéro.
  if (String(objet).trim().length > OBJET_MAX || String(texte).trim().length > TEXTE_MAX)
    return { ok: false, raison: `Courriel trop long (objet ${OBJET_MAX} caractères, texte ${TEXTE_MAX} au plus).` };
  const inconnues = [...variablesInconnues(objet), ...variablesInconnues(texte)];
  if (inconnues.length) {
    return {
      ok: false,
      raison: `Variable inconnue : ${[...new Set(inconnues)].join(", ")}. Variables possibles : ${VARIABLES_COURRIEL_FACTURE.map((v) => `{${v.cle}}`).join(", ")}.`,
    };
  }
  return { ok: true, raison: null };
}
